import { FormEvent, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

import { useAdminCreateCompanyTeamMutation } from '../../hooks/useApi';
import { getMutationErrorMessage } from '../../lib/apiErrors';
import { useTouchedFields } from '../../lib/touchedFormValidation';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { dialogFooterClassName } from '../ui/dialog-frame';
import { Field, FormErrorAlert, useFocusFirstInvalid } from '../ui/form-field';
import { ModalOverlay, modalSheetClassName } from '../ui/modal-overlay';
import { useModalDialog } from '../ui/use-modal-dialog';
import { formFieldGroupSurfaceClassName } from '../ui/formPrimitives';
import { Input } from '../ui/input';
import { dialogTitleClassName } from '../ui/typography';

interface AdminCreateTeamModalProps {
    open: boolean;
    companyId: number;
    onClose: () => void;
    onCreated?: () => void;
}

export function AdminCreateTeamModal({
    open,
    companyId,
    onClose,
    onCreated,
}: AdminCreateTeamModalProps) {
    const createTeam = useAdminCreateCompanyTeamMutation(companyId);
    const [name, setName] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const { touch, touchAll, resetTouched, fieldError } = useTouchedFields<'name'>();
    const focusFirstInvalid = useFocusFirstInvalid(formRef);
    const nameError = !name.trim() ? 'Team name is required.' : undefined;

    useEffect(() => {
        if (!open) return;
        setName('');
        setIsActive(true);
        setError(null);
        resetTouched();
    }, [open, resetTouched]);

    const { titleId, panelProps } = useModalDialog({ open, onClose });
    if (!open) return null;

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setError(null);
        touchAll(['name']);

        const trimmed = name.trim();
        if (!trimmed) {
            focusFirstInvalid();
            return;
        }

        try {
            await createTeam.mutateAsync({ name: trimmed, isActive });
            onCreated?.();
            onClose();
        } catch (err) {
            setError(getMutationErrorMessage(err, 'Could not create team. Try again.'));
        }
    };

    return (
        <ModalOverlay>
            <div
                {...panelProps}
                className={cn(modalSheetClassName, 'flex max-h-[90vh] w-full max-w-lg flex-col')}
            >
                <div className="flex shrink-0 items-start justify-between gap-4 border-b p-5">
                    <h2 id={titleId} className={dialogTitleClassName}>
                        Create team
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close dialog"
                        className="-m-1 flex size-9 shrink-0 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                    >
                        <X className="size-5" aria-hidden />
                    </button>
                </div>

                <form
                    ref={formRef}
                    className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5"
                    onSubmit={handleSubmit}
                >
                    {error ? <FormErrorAlert message={error} /> : null}

                    <Field label="Name" required error={fieldError('name', nameError)}>
                        <Input
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            onBlur={() => touch('name')}
                            placeholder="Team name"
                            autoComplete="off"
                        />
                    </Field>

                    <label className={`flex items-center gap-3 ${formFieldGroupSurfaceClassName}`}>
                        <input
                            type="checkbox"
                            checked={isActive}
                            onChange={(event) => setIsActive(event.target.checked)}
                            className="size-4 rounded border-neutral-300"
                        />
                        <span className="text-sm font-medium text-neutral-950">Active</span>
                    </label>

                    <div className={dialogFooterClassName}>
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            loading={createTeam.isPending}
                            disabled={createTeam.isPending}
                        >
                            {createTeam.isPending ? 'Creating…' : 'Create team'}
                        </Button>
                    </div>
                </form>
            </div>
        </ModalOverlay>
    );
}
